/**
 * MonitorListPage - Main page for directory monitor management
 *
 * Displays all monitors in a DataTable with sorting and pagination. Clicking
 * a row opens the monitor details dialog.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  RefreshCw,
  Download,
  Play,
  Pause,
  Edit,
  Trash2,
  AlertCircle,
  FileSearch,
} from 'lucide-react';
import { format } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import {
  useMonitors,
  useDeleteMonitor,
  useTriggerScan,
  useToggleMonitorStatus,
} from '@/features/monitors/hooks/useMonitors';
import { MonitorStatusBadge } from '@/features/monitors/components/MonitorStatusBadge';
import { MonitorTypeBadge } from '@/features/monitors/components/MonitorTypeBadge';
import { MonitorDetailsDialog } from '@/features/monitors/components/MonitorDetailsDialog';
import { useToast } from '@/hooks/useToast';
import { downloadCsv, type CsvExportColumn } from '@/lib/utils/csv-export';
import type { MonitorListItem, MonitorStatus } from '@/features/monitors/types';

const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Daily',
  hourly: 'Hourly',
  'every-15-min': 'Every 15 min',
  continuous: 'Continuous',
};

/**
 * MonitorListPage Component
 */
export const MonitorListPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'all';
  const [selectedMonitorId, setSelectedMonitorId] = useState<string | null>(null);
  const [scanningMonitors, setScanningMonitors] = useState<Set<string>>(new Set());

  const { data, isLoading, error, refetch } = useMonitors({
    status: statusFilter !== 'all' ? (statusFilter as MonitorStatus) : undefined,
  });
  const deleteMonitor = useDeleteMonitor();
  const triggerScan = useTriggerScan();
  const toggleStatus = useToggleMonitorStatus();

  const handleTriggerScan = useCallback(
    async (id: string, name: string) => {
      setScanningMonitors((prev) => new Set(prev).add(id));

      try {
        await triggerScan.mutateAsync({ monitor_id: id, force: false });
        toast({
          title: 'Scan In Progress',
          description: `Scanning "${name}"... This may take up to 60 seconds.`,
        });

        // Poll for scan completion every 5 seconds, timeout after 2 minutes
        const startTime = Date.now();
        const maxWait = 120000;

        const checkScanStatus = async () => {
          try {
            await refetch();
            const elapsed = Date.now() - startTime;
            if (elapsed >= maxWait) {
              setScanningMonitors((prev) => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
              });
              toast({
                title: 'Scan Still Running',
                description: `Scan for "${name}" is taking longer than expected. Check the scan history for status.`,
              });
              return;
            }
            setTimeout(checkScanStatus, 5000);
          } catch {
            setScanningMonitors((prev) => {
              const newSet = new Set(prev);
              newSet.delete(id);
              return newSet;
            });
          }
        };

        setTimeout(checkScanStatus, 5000);
      } catch (scanError: any) {
        setScanningMonitors((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        console.error('Failed to trigger scan:', scanError);
        toast({
          title: 'Scan Failed',
          description:
            scanError.response?.data?.detail ||
            'Failed to trigger scan. Monitor may be paused or disabled.',
          variant: 'destructive',
        });
      }
    },
    [triggerScan, toast, refetch]
  );

  const handleToggleStatus = useCallback(
    async (id: string, name: string, currentStatus: MonitorStatus) => {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      const action = newStatus === 'active' ? 'resumed' : 'paused';

      try {
        await toggleStatus.mutateAsync({
          monitor_id: id,
          status: newStatus as 'active' | 'paused',
        });
        toast({
          title: `Monitor ${action.charAt(0).toUpperCase() + action.slice(1)}`,
          description: `Monitor "${name}" has been ${action}`,
        });
      } catch (toggleError: any) {
        console.error('Failed to toggle status:', toggleError);
        toast({
          title: 'Status Update Failed',
          description:
            toggleError.response?.data?.detail || 'Failed to update monitor status',
          variant: 'destructive',
        });
      }
    },
    [toggleStatus, toast]
  );

  const handleEdit = useCallback(
    (id: string) => {
      navigate(`/monitors/${id}/edit`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    async (id: string, name: string) => {
      const confirmed = window.confirm(
        `Are you sure you want to delete monitor "${name}"?\n\n` +
          'This will stop monitoring the directory and remove all scan history. ' +
          'This action cannot be undone.'
      );
      if (!confirmed) return;

      try {
        await deleteMonitor.mutateAsync(id);
        toast({
          title: 'Monitor Deleted',
          description: `Monitor "${name}" has been deleted successfully`,
        });
      } catch (deleteError: any) {
        console.error('Failed to delete monitor:', deleteError);
        toast({
          title: 'Delete Failed',
          description:
            deleteError.response?.data?.detail ||
            'Failed to delete monitor. It may have active scans.',
          variant: 'destructive',
        });
      }
    },
    [deleteMonitor, toast]
  );

  const handleRefresh = useCallback(() => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'Monitor list has been refreshed',
    });
  }, [refetch, toast]);

  /**
   * Filter row data based on search term
   */
  const filteredData = useMemo<MonitorListItem[]>(() => {
    if (!data?.items) return [];
    if (!searchTerm) return data.items;
    const lowerSearch = searchTerm.toLowerCase();
    return data.items.filter(
      (monitor) =>
        monitor.name.toLowerCase().includes(lowerSearch) ||
        monitor.directory_path.toLowerCase().includes(lowerSearch) ||
        monitor.dataset_name?.toLowerCase().includes(lowerSearch)
    );
  }, [data, searchTerm]);

  const handleExport = useCallback(() => {
    if (!filteredData.length) {
      toast({ title: 'Nothing to export', description: 'No monitors match the current filter.' });
      return;
    }
    const csvColumns: CsvExportColumn<MonitorListItem>[] = [
      { header: 'Name', accessor: (m) => m.name },
      { header: 'Status', accessor: (m) => m.status },
      { header: 'Type', accessor: (m) => m.monitor_type },
      { header: 'Directory Path', accessor: (m) => m.directory_path },
      { header: 'Dataset', accessor: (m) => m.dataset_name ?? '' },
      { header: 'Scan Frequency', accessor: (m) => m.scan_frequency ?? '' },
      { header: 'Last Scan', accessor: (m) => m.last_scan_at ?? '' },
      { header: 'Next Scan', accessor: (m) => m.next_scan_at ?? '' },
      { header: 'Files Detected', accessor: (m) => (m as any).files_detected ?? 0 },
    ];
    downloadCsv(filteredData, csvColumns, `monitors_${format(new Date(), 'yyyy-MM-dd')}`);
    toast({ title: 'Export Complete', description: 'Monitor data exported to CSV' });
  }, [filteredData, toast]);

  const columns = useMemo<ColumnDef<MonitorListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableSortHeader column={column}>Name</DataTableSortHeader>,
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        accessorKey: 'status',
        header: ({ column }) => <DataTableSortHeader column={column}>Status</DataTableSortHeader>,
        cell: ({ row }) =>
          row.original.status ? <MonitorStatusBadge status={row.original.status} /> : null,
      },
      {
        accessorKey: 'monitor_type',
        header: ({ column }) => <DataTableSortHeader column={column}>Type</DataTableSortHeader>,
        cell: ({ row }) =>
          row.original.monitor_type ? <MonitorTypeBadge type={row.original.monitor_type} /> : null,
      },
      {
        accessorKey: 'directory_path',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Directory Path</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.directory_path;
          if (!value) return null;
          const displayPath = value.length > 50 ? `...${value.slice(-47)}` : value;
          return (
            <span className="font-mono text-sm" title={value}>
              {displayPath}
            </span>
          );
        },
      },
      {
        accessorKey: 'dataset_name',
        header: ({ column }) => <DataTableSortHeader column={column}>Dataset</DataTableSortHeader>,
        cell: ({ row }) => {
          const { dataset_id, dataset_name } = row.original;
          if (!dataset_id || !dataset_name) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            // react-router Link, not a bare <a href>: a full page load drops
            // the in-memory JWT and logs the operator out (#1029).
            <Link
              to={`/datasets/${dataset_id}`}
              className="text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {dataset_name}
            </Link>
          );
        },
      },
      {
        accessorKey: 'scan_frequency',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Scan Frequency</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.scan_frequency;
          if (!value) return null;
          return FREQUENCY_LABELS[value] || value;
        },
      },
      {
        accessorKey: 'last_scan_at',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Last Scan</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.last_scan_at;
          if (!value) return <span className="text-muted-foreground">-</span>;
          try {
            return format(new Date(value), 'MMM d, yyyy HH:mm');
          } catch {
            return value;
          }
        },
      },
      {
        accessorKey: 'next_scan_at',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Next Scan</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.next_scan_at;
          if (!value) return <span className="text-muted-foreground">-</span>;
          try {
            return format(new Date(value), 'MMM d, yyyy HH:mm');
          } catch {
            return value;
          }
        },
      },
      {
        accessorKey: 'files_detected',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Files Detected</DataTableSortHeader>
        ),
        cell: ({ row }) => (row.original as any).files_detected ?? '0',
        sortingFn: (a, b) =>
          ((a.original as any).files_detected ?? 0) - ((b.original as any).files_detected ?? 0),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const monitor = row.original;
          const isScanning = scanningMonitors.has(monitor.id);
          const isActive = monitor.status === 'active';
          return (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isScanning) handleTriggerScan(monitor.id, monitor.name);
                }}
                className="h-8 w-8 p-0"
                title={isScanning ? 'Scan in progress…' : 'Trigger Scan'}
                disabled={monitor.status === 'disabled' || isScanning}
              >
                {isScanning ? (
                  <RefreshCw className="h-4 w-4 animate-spin text-primary" />
                ) : (
                  <FileSearch className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleStatus(monitor.id, monitor.name, monitor.status);
                }}
                className="h-8 w-8 p-0"
                title={isActive ? 'Pause Monitor' : 'Resume Monitor'}
                disabled={monitor.status === 'disabled' || monitor.status === 'error'}
              >
                {isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(monitor.id);
                }}
                className="h-8 w-8 p-0"
                title="Edit"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(monitor.id, monitor.name);
                }}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
    ],
    [handleTriggerScan, handleToggleStatus, handleEdit, handleDelete, scanningMonitors]
  );

  /**
   * Empty state (no monitors at all, no filters applied)
   */
  const showEmptyState =
    !isLoading && filteredData.length === 0 && !searchTerm && statusFilter === 'all';

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <FileSearch className="h-16 w-16 text-muted-foreground mb-4" />
      <h3 className="text-xl font-semibold mb-2">No Monitors Found</h3>
      <p className="text-muted-foreground mb-6 max-w-md">
        Get started by creating your first directory monitor to automatically track and ingest
        files.
      </p>
      <Button onClick={() => navigate('/monitors/new')}>
        <Plus className="h-4 w-4 mr-2" />
        Create Monitor
      </Button>
    </div>
  );

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold">Directory Monitors</h1>
          <p className="text-muted-foreground">
            Automate file discovery and ingestion with directory monitoring
          </p>
        </div>
        <Button onClick={() => navigate('/monitors/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Monitor
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load monitors: {error.message || 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search monitors..."
          value={searchTerm}
          onChange={(e) => {
            const newParams = new URLSearchParams(searchParams);
            if (e.target.value) {
              newParams.set('search', e.target.value);
            } else {
              newParams.delete('search');
            }
            setSearchParams(newParams, { replace: true });
          }}
          className="max-w-sm"
        />
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            const newParams = new URLSearchParams(searchParams);
            if (value && value !== 'all') {
              newParams.set('status', value);
            } else {
              newParams.delete('status');
            }
            setSearchParams(newParams, { replace: true });
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={isLoading}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* DataTable or Empty State */}
      {showEmptyState ? (
        renderEmptyState()
      ) : isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          Loading…
        </div>
      ) : (
        <DataTable
          data={filteredData}
          columns={columns}
          onRowClick={(m) => setSelectedMonitorId(m.id)}
          initialSorting={[{ id: 'last_scan_at', desc: true }]}
          getRowId={(m) => m.id}
          emptyState={
            searchTerm
              ? `No monitors match "${searchTerm}".`
              : 'No monitors match the current filters.'
          }
        />
      )}

      {/* Stats */}
      {data && filteredData.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {filteredData.length} of {data.items.length} monitors
          </span>
          <span>Total: {data.total || data.items.length} monitors</span>
        </div>
      )}

      {/* Monitor Details Dialog */}
      <MonitorDetailsDialog
        monitorId={selectedMonitorId}
        onClose={() => setSelectedMonitorId(null)}
      />
    </div>
  );
};
